import React, { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Chip, Grid, Alert, Paper, Select, MenuItem, FormControl,
  InputLabel, TextField, Button, Drawer, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Tooltip, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DmsSearchBar from '../components/DmsSearchBar';
import DocumentCard from '../components/DocumentCard';
import ClassificationBadge from '../components/ClassificationBadge';
import useDmsPalette from '../hooks/useDmsPalette';

const CLASSIFICATIONS = ['unclassified', 'internal', 'confidential', 'restricted', 'secret'];
const STATUSES = ['draft', 'review', 'published', 'archived'];
const FILE_TYPES = [
  { label: 'PDF', value: 'pdf' },
  { label: 'Word', value: 'word' },
  { label: 'Excel', value: 'excel' },
  { label: 'Image', value: 'image' },
  { label: 'Other', value: 'other' },
];
const SAVED_SEARCHES_KEY = 'dms-saved-searches';

function getFileTypeGroup(mimeType = '') {
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('msword') || mimeType.includes('openxmlformats-officedocument.wordprocessing')) return 'word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'excel';
  if (mimeType.startsWith('image/')) return 'image';
  return 'other';
}

function highlightText(text, query) {
  if (!query || !text) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} style={{ background: '#ffeb3b40', color: 'inherit', borderRadius: 2 }}>{part}</mark>
      : part
  );
}

function loadSavedSearches() {
  try { return JSON.parse(localStorage.getItem(SAVED_SEARCHES_KEY)) || []; }
  catch { return []; }
}

function saveSavedSearches(searches) {
  localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(searches));
}

export default function SearchResults({ onDocumentSelect, initialQuery }) {
  const MP = useDmsPalette();
  const [searchParams] = useSearchParams();
  const queryFromUrl = searchParams.get('q') || '';
  const [searchData, setSearchData] = useState(null);
  const [classification, setClassification] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [fileType, setFileType] = useState('');
  const [savedSearches, setSavedSearches] = useState(loadSavedSearches);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleResults = useCallback((data) => {
    setSearchData(data);
  }, []);

  const rawResults = useMemo(() => searchData?.results || [], [searchData]);

  const filteredResults = useMemo(() => {
    return rawResults.filter(doc => {
      if (classification && doc.classification?.toLowerCase() !== classification) return false;
      if (status && doc.status !== status) return false;
      if (fileType && getFileTypeGroup(doc.mimeType) !== fileType) return false;
      if (dateFrom && new Date(doc.createdAt) < new Date(dateFrom)) return false;
      if (dateTo && new Date(doc.createdAt) > new Date(dateTo + 'T23:59:59')) return false;
      return true;
    });
  }, [rawResults, classification, status, fileType, dateFrom, dateTo]);

  const hasSearched = searchData !== null;
  const query = searchData?.query || '';

  const handleSaveSearch = () => {
    if (!query) return;
    const entry = {
      id: Date.now(),
      query,
      filters: { classification, status, dateFrom, dateTo, fileType },
      savedAt: new Date().toISOString(),
    };
    const updated = [entry, ...savedSearches.slice(0, 9)];
    setSavedSearches(updated);
    saveSavedSearches(updated);
  };

  const handleDeleteSaved = (id) => {
    const updated = savedSearches.filter(s => s.id !== id);
    setSavedSearches(updated);
    saveSavedSearches(updated);
  };

  const handleLoadSaved = (saved) => {
    setClassification(saved.filters.classification || '');
    setStatus(saved.filters.status || '');
    setDateFrom(saved.filters.dateFrom || '');
    setDateTo(saved.filters.dateTo || '');
    setFileType(saved.filters.fileType || '');
    setDrawerOpen(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
            Search Documents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search across all vaults using keywords or AI-powered natural language
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {query && (
            <Tooltip title="Save this search">
              <Button size="small" startIcon={<SaveIcon />} onClick={handleSaveSearch} variant="outlined">
                Save Search
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Saved searches">
            <IconButton onClick={() => setDrawerOpen(true)} size="small">
              <BookmarkIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <DmsSearchBar onResults={handleResults} initialQuery={initialQuery || queryFromUrl} />

      {/* AI Interpretation Bar */}
      {hasSearched && searchData.isAiSearch && searchData.aiInterpretation && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'primary.dark', borderRadius: 2 }}>
          <Typography variant="body2">
            🧠 AI interpreted: <strong>"{query}"</strong> → vault: {searchData.aiInterpretation.vault || 'any'},{' '}
            topic: {searchData.aiInterpretation.topic || 'general'},{' '}
            classification: {searchData.aiInterpretation.classification || 'any'}
          </Typography>
        </Paper>
      )}

      {/* Faceted Filters */}
      {hasSearched && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, mt: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Classification</InputLabel>
            <Select value={classification} onChange={e => setClassification(e.target.value)} label="Classification">
              <MenuItem value="">Any</MenuItem>
              {CLASSIFICATIONS.map(c => (
                <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={e => setStatus(e.target.value)} label="Status">
              <MenuItem value="">Any</MenuItem>
              {STATUSES.map(s => (
                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>File Type</InputLabel>
            <Select value={fileType} onChange={e => setFileType(e.target.value)} label="File Type">
              <MenuItem value="">Any</MenuItem>
              {FILE_TYPES.map(ft => (
                <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small" type="date" label="From" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }}
          />
          <TextField
            size="small" type="date" label="To" value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }}
          />
          {(classification || status || fileType || dateFrom || dateTo) && (
            <Button size="small" onClick={() => { setClassification(''); setStatus(''); setFileType(''); setDateFrom(''); setDateTo(''); }}>
              Clear Filters
            </Button>
          )}
        </Box>
      )}

      {/* Result metadata */}
      {hasSearched && !searchData.error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Chip
            label={`${filteredResults.length} result${filteredResults.length !== 1 ? 's' : ''}${rawResults.length !== filteredResults.length ? ` (filtered from ${rawResults.length})` : ''}`}
            size="small"
            sx={{ bgcolor: MP.surface, fontWeight: 600 }}
          />
          {searchData.time != null && (
            <Typography variant="caption" color="text.secondary">
              in {(searchData.time / 1000).toFixed(2)}s
            </Typography>
          )}
          {query && (
            <Chip
              label={`"${query}"`}
              size="small"
              variant="outlined"
              onDelete={() => setSearchData(null)}
              sx={{ maxWidth: 200 }}
            />
          )}
        </Box>
      )}

      {/* Error state */}
      {searchData?.error && (
        <Alert severity="error" sx={{ mt: 2 }}>{searchData.error}</Alert>
      )}

      {/* Empty state */}
      {!hasSearched && (
        <Box sx={{ textAlign: 'center', mt: 10, color: MP.textSecondary }}>
          <SearchIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
          <Typography variant="h6" fontWeight={600}>Search across all vaults</Typography>
          <Typography variant="body2" sx={{ mt: 1, maxWidth: 400, mx: 'auto' }}>
            Type a keyword to search, or switch to AI Search for natural language queries like
            "Find contracts expiring this quarter"
          </Typography>
        </Box>
      )}

      {/* No results */}
      {hasSearched && !searchData.error && filteredResults.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 8, color: MP.textSecondary }}>
          <Typography variant="h6">No documents found</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>Try different keywords or adjust filters</Typography>
        </Box>
      )}

      {/* Results grid */}
      {filteredResults.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {filteredResults.map(doc => {
            const score = doc.relevanceScore ?? doc.score;
            return (
              <Grid item xs={12} sm={6} md={4} key={doc._id}>
                <Box sx={{ position: 'relative' }}>
                  {/* Relevance score badge */}
                  {score != null && (
                    <Chip
                      label={`${Math.round(score * 100)}% match`}
                      size="small"
                      sx={{
                        position: 'absolute', top: 8, right: 8, zIndex: 1,
                        bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.65rem', height: 18,
                      }}
                    />
                  )}
                  {/* Classification badge */}
                  {doc.classification && (
                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                      <ClassificationBadge classification={doc.classification} />
                    </Box>
                  )}
                  <DocumentCard
                    doc={{
                      ...doc,
                      title: query ? undefined : doc.title, // pass raw title; highlight below
                    }}
                    onSelect={onDocumentSelect || (() => {})}
                    onEdit={() => onDocumentSelect?.(doc)}
                    onDelete={() => {}}
                    titleOverride={query ? highlightText(doc.title, query) : undefined}
                    descriptionOverride={query && doc.description ? highlightText(doc.description, query) : undefined}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Saved Searches Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            <BookmarkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Saved Searches
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {savedSearches.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              No saved searches yet. Run a search and click "Save Search".
            </Typography>
          ) : (
            <List disablePadding>
              {savedSearches.map(saved => (
                <ListItem
                  key={saved.id}
                  button
                  onClick={() => handleLoadSaved(saved)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={`"${saved.query}"`}
                    secondary={Object.entries(saved.filters)
                      .filter(([, v]) => v)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(', ') || 'No filters'}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" edge="end" onClick={e => { e.stopPropagation(); handleDeleteSaved(saved.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
